import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core';
import { takeUntil } from 'rxjs';
import { TuiSizeXS, TuiSizeXL } from '@taiga-ui/core';
import { TranslateService } from '@ngx-translate/core';
import { LoyaltyFavoriteProductAddRequestDto } from '@src/api';
import { AlertService } from '@src/core/services';
import { BaseResizableComponent } from '@src/app/modules/base';

import { LoyaltyProgramApiService } from '../../services';
import { LoyaltyProgramListProduct } from '../../models';

@Component({
  selector: 'lp-favorite-button',
  templateUrl: './favorite-button.component.html',
  styleUrls: ['./favorite-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FavoriteButtonComponent extends BaseResizableComponent {
  private readonly lpApiService = inject(LoyaltyProgramApiService);
  private readonly alertService = inject(AlertService);
  private readonly translateService = inject(TranslateService);

  @Input() size: TuiSizeXS | TuiSizeXL = 's';
  @Input() product?: LoyaltyProgramListProduct;
  @Input() appearance: 'icon' | 'block' = 'icon';

  get isFavorite() {
    return !!this.product?.isFavorite;
  }

  get dataAppearance(): string {
    if (this.appearance === 'block') {
      return this.isFavorite ? 'lp-icon-block-filled-pink' : 'lp-icon-block';
    }

    return this.isFavorite ? 'lp-icon-filled-pink' : 'lp-icon';
  }

  switchFavorite(click: MouseEvent) {
    click.stopPropagation();
    if (!this.product) return;

    const product = <LoyaltyFavoriteProductAddRequestDto>{
      productId: this.product.id,
      unionId: this.product.unionId,
    };

    if (this.product.isFavorite) {
      this.lpApiService
        .removeProductFromFavorite(product)
        .pipe(takeUntil(this.destroyed$$))
        .subscribe(() => {
          this.alertService.success(
            this.translateService.instant('components.loyaltyProgram.alerts.successes.removeFromFavorite'),
          );

          if (this.product) {
            this.product.isFavorite = false;
          }

          this.cdr.markForCheck();
        });
    } else {
      this.lpApiService
        .addProductToFavorite(product)
        .pipe(takeUntil(this.destroyed$$))
        .subscribe(() => {
          this.alertService.success(
            this.translateService.instant('components.loyaltyProgram.alerts.successes.addToFavorite'),
          );

          if (this.product) {
            this.product.isFavorite = true;
          }

          this.cdr.markForCheck();
        });
    }
  }
}
